import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Form } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Loader from '../../components/Loader'
import Message from '../../components/Message'
import { register } from '../../actions/userActions'
import { USER_REGISTER_RESET } from '../../constants/userConstants'
import { FEATURES } from '../../config/features'

// Typ „worker” (młodociany) ukryty przez FEATURES.youth — konta i kod zostają
const VISIBLE_USER_TYPES = [
  { value: 'employer', label: 'Pracodawca' },
  ...(FEATURES.youth ? [{ value: 'worker', label: 'Młodociany pracownik / uczeń' }] : []),
  { value: 'adult_worker', label: FEATURES.youth ? 'Dorosły pracownik' : 'Pracownik' },
]
const DEFAULT_USER_TYPE = FEATURES.youth ? 'worker' : 'adult_worker'

function Register() {
  const [name, setName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [message, setMessage] = useState('')
  const [searchParams] = useSearchParams()
  const [userType, setUserType] = useState(() => {
    const requested = searchParams.get('type')
    return VISIBLE_USER_TYPES.some(t => t.value === requested) ? requested : DEFAULT_USER_TYPE
  })
  const [companyName, setCompanyName] = useState('')
  const [nip, setNip] = useState('')
  const [companyCity, setCompanyCity] = useState('')
  const [companyPhone, setCompanyPhone] = useState('')

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const userRegister = useSelector(state => state.userRegister)
  const { error, loading, userInfo } = userRegister

  useEffect(() => {
    dispatch({ type: USER_REGISTER_RESET })
  }, [dispatch])

  useEffect(() => {
    if (userInfo) {
      let redirectPath;
      if (userInfo.user_type === 'employer') {
        redirectPath = '/employer-home';
      } else if (userInfo.user_type === 'adult_worker') {
        redirectPath = '/link-requests';
      } else {
        redirectPath = '/home-screen';
      }
      navigate(redirectPath)
    }
  }, [navigate, userInfo])

  const submitHandler = (e) => {
    e.preventDefault()

    if (password !== confirmPassword) {
      setMessage('Hasła nie są takie same')
    } else if (!email) {
      setMessage('Email jest wymagany')
    } else {
      // Walidacja formatu emaila
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setMessage('Proszę podać prawidłowy adres email')
        return
      }

      if (userType === 'employer' && (!companyName || !nip)) {
        setMessage('Nazwa firmy i NIP są wymagane dla pracodawcy')
      } else {
        setMessage('')
        const payload = {
          name,
          lastName,
          email,
          password,
          user_type: userType
        }

        if (userType === 'employer') {
          payload.company_name = companyName
          payload.nip = nip
          payload.company_city = companyCity
          payload.company_phone = companyPhone
        }

        dispatch(register(payload))
      }
    }
  }

  const isEmployer = userType === 'employer'

  return (
    <div className="pub-form-box">
      <h1 className="pub-h2">{isEmployer ? 'Załóż konto firmy' : 'Załóż konto'}</h1>
      <p className="pub-muted" style={{ marginBottom: 28 }}>
        {isEmployer
          ? 'Pierwszy pracownik za darmo, bez karty i bez limitu czasu.'
          : 'Konto pracownika jest bezpłatne.'}
      </p>

      <div className="pub-card">
        {message && <Message variant='danger'>{message}</Message>}
        {error && <Message variant='danger'>{error}</Message>}
        {loading && <Loader />}

        <Form onSubmit={submitHandler}>
          <Form.Group className="mb-4">
            <Form.Label>Rodzaj konta</Form.Label>
            <div className="d-flex flex-wrap gap-4">
              {VISIBLE_USER_TYPES.map(t => (
                <Form.Check
                  key={t.value}
                  type="radio"
                  label={t.label}
                  name="userType"
                  value={t.value}
                  checked={userType === t.value}
                  onChange={(e) => setUserType(e.target.value)}
                  id={t.value}
                />
              ))}
            </div>
          </Form.Group>

          <Form.Group className="mb-3" controlId="register-name">
            <Form.Label>Imię</Form.Label>
            <Form.Control type='text' placeholder='Jak masz na imię?' value={name}
              onChange={(e) => setName(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="register-last-name">
            <Form.Label>Nazwisko</Form.Label>
            <Form.Control type='text' placeholder='Twoje nazwisko' value={lastName}
              onChange={(e) => setLastName(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="register-email">
            <Form.Label>Adres e-mail</Form.Label>
            <Form.Control type='email' placeholder='twoj.email@example.com' value={email}
              onChange={(e) => setEmail(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="register-password">
            <Form.Label>Hasło</Form.Label>
            <Form.Control type='password' placeholder='Minimum 8 znaków' value={password}
              onChange={(e) => setPassword(e.target.value)} required />
          </Form.Group>

          <Form.Group className="mb-3" controlId="register-confirm">
            <Form.Label>Potwierdź hasło</Form.Label>
            <Form.Control type='password' placeholder='Wpisz hasło ponownie' value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)} required />
          </Form.Group>

          {isEmployer && (
            <>
              <h2 className="pub-h3" style={{ marginTop: 28 }}>Dane firmy</h2>

              <Form.Group className="mb-3" controlId="register-company">
                <Form.Label>Nazwa firmy</Form.Label>
                <Form.Control type='text' placeholder='Nazwa Twojej firmy' value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3" controlId="register-nip">
                <Form.Label>NIP</Form.Label>
                <Form.Control type='text' placeholder='10-cyfrowy numer NIP' value={nip}
                  onChange={(e) => setNip(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  required maxLength='10' />
                <Form.Text className="pub-small">Bez myślników.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-3" controlId="register-city">
                <Form.Label>Miasto siedziby</Form.Label>
                <Form.Control type='text' placeholder='Miasto' value={companyCity}
                  onChange={(e) => setCompanyCity(e.target.value)} required />
              </Form.Group>

              <Form.Group className="mb-3" controlId="register-phone">
                <Form.Label>Telefon do firmy</Form.Label>
                <Form.Control type='tel' placeholder='Numer telefonu' value={companyPhone}
                  onChange={(e) => setCompanyPhone(e.target.value)} />
              </Form.Group>
            </>
          )}

          <p className="pub-small" style={{ margin: '20px 0' }}>
            Zakładając konto, akceptujesz <Link to="/terms">Regulamin</Link> i{' '}
            <Link to="/privacy">Politykę prywatności</Link>.
          </p>

          <button type="submit" className="pub-btn w-100">Utwórz konto</button>
        </Form>
      </div>

      <p className="pub-muted" style={{ marginTop: 24 }}>
        Masz już konto? <Link to="/login" className="pub-link">Zaloguj się</Link>
      </p>
    </div>
  )
}

export default Register
